import { ArrowRight } from "lucide-react";
import { Link } from "react-router";
import { industryById, type CaseStudy } from "@/content/case-studies";
import { cn } from "@/lib/utils";

interface CaseStudyCardProps {
  study: CaseStudy;
  className?: string;
}

export function CaseStudyCard({ study, className }: CaseStudyCardProps) {
  const industry = industryById(study.industryId);
  // Long metrics like "Days, not quarters" need a smaller type size than "95%".
  const compactMetric = study.metric.value.length > 6;

  return (
    <Link
      to={`/case-studies/${study.slug}`}
      aria-label={`Read case study: ${study.headline}`}
      className={cn(
        "group flex flex-col bg-black border border-[#353739] rounded-[1.75rem] p-7 transition-all duration-300 hover:border-[#555759] hover:-translate-y-0.5",
        className,
      )}
    >
      {/* Industry + read time */}
      <div className="mb-5 flex items-center justify-between gap-2">
        <span className="whitespace-nowrap rounded-full border border-[#353739] px-2.5 py-1 text-[0.66rem] uppercase tracking-wide text-muted-foreground">
          {industry.label}
        </span>
        <span className="whitespace-nowrap text-xs text-[#7c7e80]">
          {study.readTime}
        </span>
      </div>

      {/* Headline — full sentence, so font-body (Inter), not Azonix */}
      <h3 className="mb-3 font-body text-[1.02rem] font-semibold leading-snug text-[#f2f2f2]">
        {study.headline}
      </h3>

      <p className="mb-5 line-clamp-3 font-body text-sm font-light leading-relaxed text-[#b3b3b3]">
        {study.summary}
      </p>

      {/* Metric — the visual hero */}
      <div className="mt-auto">
        <div
          className={cn(
            "bg-gradient-to-b from-white to-[#9a9c9e] bg-clip-text font-body font-bold leading-none tracking-tight text-transparent",
            compactMetric ? "text-2xl leading-tight" : "text-5xl",
          )}
        >
          {study.metric.value}
        </div>
        <div className="mt-2 text-xs font-light leading-snug text-[#b3b3b3]">
          {study.metric.caption}
        </div>
      </div>

      {/* Tech tags */}
      <div className="mt-5 flex flex-wrap gap-1.5">
        {study.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-md border border-[#2a2c2e] bg-[#0c0d0e] px-2 py-1 font-mono text-[0.65rem] text-[#b3b3b3]"
          >
            {tag}
          </span>
        ))}
      </div>

      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[#f2f2f2]">
        Read{" "}
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}
